import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Translation Levels System
 * Defines the quality and completeness of translations
 * 
 * NEXORA AI Translation Philosophy:
 * We believe in transparent, community-driven translations that honor
 * both our official languages and our cultural roots in the Indian Ocean.
 */
export const TRANSLATION_LEVELS = {
  OFFICIAL: 'official',         // Official NEXORA AI languages, 100% perfect (EN, FR)
  FOUNDING: 'founding',          // Founding languages from Indian Ocean roots (Kréol, Shikomori, Kiswahili)
  COMMUNITY: 'community',        // Community-driven translations, validated
  EXPERIMENTAL: 'experimental'   // Experimental translations, work in progress
};

/**
 * Geographic Regions with associated languages
 * Used for intelligent language suggestions during onboarding
 */
export const REGIONS = {
  INDIAN_OCEAN: {
    code: 'io',
    name: 'Indian Ocean',
    nativeName: 'Océan Indien',
    languages: ['fr', 'en', 'rcf', 'km', 'sw', 'mg'],
    priority: ['fr', 'en'] // Show first in selection
  },
  EUROPE: {
    code: 'eu',
    name: 'Europe',
    nativeName: 'Europe',
    languages: ['en', 'fr', 'de', 'it', 'es', 'pt', 'nl', 'pl', 'ru', 'uk'],
    priority: ['en', 'fr', 'de']
  },
  AFRICA: {
    code: 'af',
    name: 'Africa',
    nativeName: 'Afrique',
    languages: ['fr', 'en', 'sw', 'ar', 'af', 'mg'],
    priority: ['en', 'fr', 'sw']
  },
  ASIA: {
    code: 'as',
    name: 'Asia',
    nativeName: 'Asie',
    languages: ['en', 'zh', 'ja', 'ko', 'hi', 'bn', 'ta', 'th', 'vi', 'id', 'ms'],
    priority: ['en', 'zh', 'ja']
  },
  MIDDLE_EAST: {
    code: 'me',
    name: 'Middle East',
    nativeName: 'Moyen-Orient',
    languages: ['ar', 'fa', 'en'],
    priority: ['ar', 'en']
  },
  AMERICAS: {
    code: 'am',
    name: 'Americas',
    nativeName: 'Amériques',
    languages: ['en', 'es', 'pt', 'fr'],
    priority: ['en', 'es', 'pt']
  },
  OCEANIA: {
    code: 'oc',
    name: 'Oceania',
    nativeName: 'Océanie',
    languages: ['en', 'fr'],
    priority: ['en']
  },
  GLOBAL: {
    code: 'global',
    name: 'Global',
    nativeName: 'Global',
    languages: Object.keys({}), // Will be populated with all languages
    priority: ['en', 'fr']
  }
};

/**
 * Supported Languages with metadata
 * 
 * Structure:
 * - name: English name
 * - nativeName: Native language name
 * - flag: Emoji flag
 * - rtl: Right-to-left text direction
 * - level: Translation completeness level
 * - regions: Associated geographic regions
 */
export const LANGUAGES = {
  // ========== OFFICIAL LANGUAGES (100% Perfect) ==========
  en: { 
    name: 'English', 
    nativeName: 'English', 
    flag: '🇬🇧', 
    rtl: false, 
    level: TRANSLATION_LEVELS.OFFICIAL,
    regions: ['io', 'eu', 'af', 'as', 'me', 'am', 'oc', 'global'],
    contributors: 0
  },
  fr: { 
    name: 'French', 
    nativeName: 'Français', 
    flag: '🇫🇷', 
    rtl: false, 
    level: TRANSLATION_LEVELS.OFFICIAL,
    regions: ['io', 'eu', 'af', 'am', 'global'],
    contributors: 0
  },
  
  // ========== FOUNDING LANGUAGES (Indian Ocean Roots) ==========
  // These languages represent NEXORA AI's cultural heritage
  rcf: { 
    name: 'Réunion Creole', 
    nativeName: 'Kréol Réyoné', 
    flag: '🇷🇪', 
    rtl: false, 
    level: TRANSLATION_LEVELS.FOUNDING,
    regions: ['io'],
    contributors: 0,
    progress: 0,
    founding: true // Special flag for founding languages
  },
  km: { 
    name: 'Comorian', 
    nativeName: 'Shikomori', 
    flag: '🇰🇲', 
    rtl: false, 
    level: TRANSLATION_LEVELS.FOUNDING,
    regions: ['io', 'af'],
    contributors: 0,
    progress: 0,
    founding: true
  },
  sw: { 
    name: 'Swahili', 
    nativeName: 'Kiswahili', 
    flag: '🇰🇪', 
    rtl: false, 
    level: TRANSLATION_LEVELS.FOUNDING,
    regions: ['io', 'af'],
    contributors: 0,
    progress: 0,
    founding: true
  },
  
  // ========== COMMUNITY LANGUAGES (Validated Community Contributions) ==========
  // Languages promoted from Experimental after reaching quality thresholds
  // TODO: Move languages here as they reach 90%+ with community validation
  
  // ========== EXPERIMENTAL LANGUAGES (Work in Progress) ==========
  // International languages with structure ready for contributions
  es: { 
    name: 'Spanish', 
    nativeName: 'Español', 
    flag: '🇪🇸', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu', 'am', 'global'],
    contributors: 0,
    progress: 0
  },
  pt: { 
    name: 'Portuguese', 
    nativeName: 'Português', 
    flag: '🇵🇹', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu', 'am', 'global'],
    contributors: 0,
    progress: 0
  },
  de: { 
    name: 'German', 
    nativeName: 'Deutsch', 
    flag: '🇩🇪', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu', 'global'],
    contributors: 0,
    progress: 0
  },
  it: { 
    name: 'Italian', 
    nativeName: 'Italiano', 
    flag: '🇮🇹', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu', 'global'],
    contributors: 0,
    progress: 0
  },
  nl: { 
    name: 'Dutch', 
    nativeName: 'Nederlands', 
    flag: '🇳🇱', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu'],
    contributors: 0,
    progress: 0
  },
  pl: { 
    name: 'Polish', 
    nativeName: 'Polski', 
    flag: '🇵🇱', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu'],
    contributors: 0,
    progress: 0
  },
  ru: { 
    name: 'Russian', 
    nativeName: 'Русский', 
    flag: '🇷🇺', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu', 'as'],
    contributors: 0,
    progress: 0
  },
  uk: { 
    name: 'Ukrainian', 
    nativeName: 'Українська', 
    flag: '🇺🇦', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['eu'],
    contributors: 0,
    progress: 0
  },
  ar: { 
    name: 'Arabic', 
    nativeName: 'العربية', 
    flag: '🇸🇦', 
    rtl: true, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['me', 'af', 'global'],
    contributors: 0,
    progress: 0
  },
  fa: { 
    name: 'Persian', 
    nativeName: 'فارسی', 
    flag: '🇮🇷', 
    rtl: true, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['me'],
    contributors: 0,
    progress: 0
  },
  zh: { 
    name: 'Chinese', 
    nativeName: '中文', 
    flag: '🇨🇳', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as', 'global'],
    contributors: 0,
    progress: 0
  },
  ja: { 
    name: 'Japanese', 
    nativeName: '日本語', 
    flag: '🇯🇵', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as', 'global'],
    contributors: 0,
    progress: 0
  },
  ko: { 
    name: 'Korean', 
    nativeName: '한국어', 
    flag: '🇰🇷', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  hi: { 
    name: 'Hindi', 
    nativeName: 'हिन्दी', 
    flag: '🇮🇳', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  bn: { 
    name: 'Bengali', 
    nativeName: 'বাংলা', 
    flag: '🇧🇩', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  ta: { 
    name: 'Tamil', 
    nativeName: 'தமிழ்', 
    flag: '🇮🇳', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  th: { 
    name: 'Thai', 
    nativeName: 'ไทย', 
    flag: '🇹🇭', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  vi: { 
    name: 'Vietnamese', 
    nativeName: 'Tiếng Việt', 
    flag: '🇻🇳', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  id: { 
    name: 'Indonesian', 
    nativeName: 'Bahasa Indonesia', 
    flag: '🇮🇩', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  ms: { 
    name: 'Malay', 
    nativeName: 'Bahasa Melayu', 
    flag: '🇲🇾', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['as'],
    contributors: 0,
    progress: 0
  },
  mg: { 
    name: 'Malagasy', 
    nativeName: 'Malagasy', 
    flag: '🇲🇬', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['io', 'af'],
    contributors: 0,
    progress: 0
  },
  af: { 
    name: 'Afrikaans', 
    nativeName: 'Afrikaans', 
    flag: '🇿🇦', 
    rtl: false, 
    level: TRANSLATION_LEVELS.EXPERIMENTAL,
    regions: ['af'],
    contributors: 0,
    progress: 0
  }
};

// Populate GLOBAL region with all languages
REGIONS.GLOBAL.languages = Object.keys(LANGUAGES);

/**
 * Translation Namespaces
 * Modular organization of translations by feature/section
 */
export const NAMESPACES = [
  'common',        // Shared UI elements (buttons, labels, errors)
  'auth',          // Authentication (login, register, password)
  'dashboard',     // Main dashboard
  'settings',      // User settings
  'onboarding',    // Onboarding flow
  'products',      // Products management
  'posts',         // Posts management
  'analytics',     // Analytics & reporting
  'admin',         // Admin dashboard
  'notifications', // Notifications center
  'landing',       // Landing page (Hero, Product Demo, Why NEXORA, AI Features)
  'workspaces',    // Workspaces management (LOT 3.1)
  'imageUploader'  // Image Uploader component (Platform-wide reference component)
];

// Default namespace and language
export const DEFAULT_NAMESPACE = 'common';
export const DEFAULT_LANGUAGE = 'en';

/**
 * Helper: Get languages by level
 */
export const getLanguagesByLevel = (level) => {
  return Object.entries(LANGUAGES)
    .filter(([_, lang]) => lang.level === level)
    .map(([code, lang]) => ({ code, ...lang }));
};

/**
 * Helper: Get founding languages (special group)
 */
export const getFoundingLanguages = () => {
  return Object.entries(LANGUAGES)
    .filter(([_, lang]) => lang.founding === true)
    .map(([code, lang]) => ({ code, ...lang }));
};

/**
 * Helper: Get languages by region
 */
export const getLanguagesByRegion = (regionCode) => {
  const region = Object.values(REGIONS).find(r => r.code === regionCode);
  if (!region) return [];
  
  return region.languages.map(langCode => ({
    code: langCode,
    ...LANGUAGES[langCode]
  }));
};

/**
 * Helper: Detect browser language
 */
export const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0]; // 'en-US' -> 'en'
  return LANGUAGES[langCode] ? langCode : DEFAULT_LANGUAGE;
};

/**
 * Helper: Detect browser timezone
 */
export const detectBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Load translation resources
 * Imports all translation files for all languages and namespaces
 */
const loadResources = () => {
  const resources = {};
  
  Object.keys(LANGUAGES).forEach(lang => {
    resources[lang] = {};
    NAMESPACES.forEach(ns => {
      try {
        resources[lang][ns] = require(`./locales/${lang}/${ns}.json`);
      } catch (error) {
        // File doesn't exist yet (experimental/community translations)
        // Fallback to English or show key
        resources[lang][ns] = {};
      }
    });
  });
  
  return resources;
};

/**
 * Initialize i18next
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: loadResources(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: Object.keys(LANGUAGES),
    defaultNS: DEFAULT_NAMESPACE,
    ns: NAMESPACES,
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'nexora_language',
    },
    
    interpolation: {
      escapeValue: false, // React already escapes
    },
    
    react: {
      useSuspense: true,
    },
    
    // Show keys for missing translations (helpful for development)
    saveMissing: false,
    missingKeyHandler: (lngs, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] Missing: ${key} in ${ns} for ${lngs}`);
      }
    },
  }).then(() => {
    // Sync <html lang> on initial page load/after refresh
    document.documentElement.lang = i18n.language;
  });

// Sync <html lang> attribute on active language changes (button click)
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
